import { Tooltip } from "antd";
import formatarDuracao from "./formatarDuracao";

export const BREAKPOINTS = {
  xs: 0, // Extra Small (less than 576px)
  sm: 576, // Small (equal to or greater than 576px)
  md: 768, // Medium (equal to or greater than 768px)
  lg: 992, // Large (equal to or greater than 992px)
  xl: 1200, // Extra Large (equal to or greater than 1200px)
};

export const VIBRANT_COLORS = [
  { name: "Electric Blue", code: "#7DF9FF" },
  { name: "Neon Green", code: "#39FF14" },
  { name: "Fuchsia", code: "#FF00FF" },
  { name: "Electric Yellow", code: "#FFFF00" },
  { name: "Electric Orange", code: "#FFA500" },
  { name: "Electric Purple", code: "#BF00FF" },
  { name: "Cyan", code: "#00FFFF" },
  { name: "Magenta", code: "#FF00FF" },
  { name: "Hot Pink", code: "#FF69B4" },
  { name: "Turquoise", code: "#40E0D0" },
];

const parseNumberValue = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return value;

  const sanitized = String(value).replace(/[^\d,.-]/g, "");
  if (!sanitized) return 0;

  const isNegative = sanitized.startsWith("-");
  const unsigned = sanitized.replace(/-/g, "");
  const commaCount = (unsigned.match(/,/g) || []).length;
  const dotCount = (unsigned.match(/\./g) || []).length;

  let normalized = unsigned;
  if (commaCount > 0 && dotCount > 0) {
    normalized = unsigned.replace(/\./g, "").replace(",", ".");
  } else if (commaCount > 0) {
    if (commaCount > 1) {
      const parts = unsigned.split(",");
      const decimalPart = parts.pop();
      normalized = `${parts.join("")}.${decimalPart}`;
    } else {
      normalized = unsigned.replace(",", ".");
    }
  } else if (dotCount > 1) {
    normalized = unsigned.replace(/\./g, "");
  } else if (dotCount === 1) {
    const [integerPart, decimalPart] = unsigned.split(".");
    if (decimalPart?.length === 3 && integerPart !== "0") {
      normalized = `${integerPart}${decimalPart}`;
    }
  }

  const signedValue = isNegative ? `-${normalized}` : normalized;
  const parsed = Number.parseFloat(signedValue);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const parsePercentValue = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return value;

  const sanitized = String(value).replace(/[^\d,.-]/g, "");
  if (!sanitized) return 0;

  const hasComma = sanitized.includes(",");
  const hasDot = sanitized.includes(".");

  let normalized = sanitized;
  if (hasComma && hasDot) {
    normalized = sanitized.replace(/\./g, "").replace(",", ".");
  } else if (hasComma) {
    normalized = sanitized.replace(",", ".");
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatOneDecimalPtBr = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

const formatIntegerPtBr = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

export const HOME_DASHBOARD_METRICS_DESCRIPTION = {
  faturamento_total:
    "Faturamento total da loja durante o período de análise. São consideradas apenas as vendas presenciais.",
  receita_tempo_permanencia:
    "Receita calculada pelo tempo de atenção em horas nas zonas da loja.",
  oportunidades:
    "Oportunidades de venda: clientes que entraram na loja. Vendedores não contam, e quem entra junto conta como uma oportunidade só.",
  conversao:
    "Métrica que relaciona a quantidade de vendas efetuadas pela quantidade de pessoas que entram na loja.",
  conversao_oportunidades:
    "Vendas presenciais dos dias com entrada divididas pelas oportunidades da loja.",
};

export const FLOW_DETECTIONS_METRICS = [
  {
    key: "total_oportunidades",
    title: "Oportunidades",
    description:
      "Oportunidades de venda: clientes que entraram na loja. Vendedores não contam, e quem entra junto conta como uma oportunidade só.",
    suffix: "",
  },
  {
    key: "occupied_percent",
    title: "Ocupação da Loja",
    description:
      "Porcentagem do tempo total em que a loja esteve ocupada. É calculada pela contagem dos minutos com pelo menos uma pessoa dividida pelo total de minutos em que a loja esteve aberta, com base no horário de funcionamento. Essa métrica não é afetada pelos filtros de zonas e categoria, apenas pelos filtros de período e data.",
    suffix: "%",
  },
  {
    key: "max_people",
    title: "Pico de pessoas",
    description:
      "Número máximo (pico) simultâneo de pessoas, considerando todas as zonas da loja. Essa métrica não é afetada pelos filtros de zonas e categoria, apenas pelos filtros de período e data.",
    suffix: "",
  },
  {
    key: "avg_occupied_time",
    title: "Tempo médio de atenção por zona",
    description:
      "Tempo de atenção acumulado das zonas selecionadas dividido pela quantidade de zonas distintas. Zonas de staff e de fila ficam fora, a menos que sejam escolhidas no filtro.",
    suffix: "",
    formatter: "duration",
  },
  {
    key: "avg_queue_wait",
    title: "Tempo médio de Fila",
    description:
      "Tempo médio de espera nas zonas marcadas como Fila. Calculado por sessões de cada pessoa (gap > 60 s = nova sessão; sessões < 15 s são descartadas).",
    suffix: "",
    formatter: "duration",
  },
];

export const REVENUE_KPIS_METRICS = [
  {
    key: "faturamento_total",
    title: "Faturamento Total",
    description:
      "Faturamento total da loja selecionada durante o período de análise. São consideradas apenas as vendas presenciais.",
    suffix: "",
    formatter: "currency",
  },
  {
    key: "faturamento_por_tempo_permanencia",
    title: "Faturamento por Atenção",
    description:
      HOME_DASHBOARD_METRICS_DESCRIPTION["receita_tempo_permanencia"],
    suffix: "R$/min",
    formatter: "number_br",
  },
  {
    key: "ticket_medio",
    title: "Ticket médio",
    description:
      "Valor médio faturado por venda. É calculado pelo faturamento total dividido pela quantidade de vendas.",
    suffix: "",
    formatter: "currency",
  },
  {
    key: "valor_medio_faturado_item",
    title: "Faturamento Médio por Item",
    description: "Valor da média de cada item vendido na loja.",
    suffix: "",
    formatter: "currency",
  },
  {
    key: "quantidade_de_vendas",
    title: "Quantidade de Vendas",
    description:
      "Quantidade total de vendas presenciais da loja selecionada no intervalo de datas filtrado.",
    suffix: "",
  },
  {
    key: "conversao",
    title: "Conversão",
    description:
      "Vendas presenciais dos dias com entrada divididas pelas oportunidades da loja. É da loja inteira: não muda com os filtros de zona, coleção e vendedor.",
    suffix: "%",
  },
  {
    key: "percent_vendas_sem_zona",
    title: "% Vendas estoque",
    description:
      "Porcentagem do faturamento total presencial originada de produtos sem nenhuma zona vinculada. Exibido apenas quando todas as zonas e cole\u00e7\u00f5es est\u00e3o selecionadas.",
    suffix: "%",
  },
];

export const PEAK_HOURS_METRICS = [
  {
    key: "occupied_time_accum",
    title: "Tempo de atenção acumulado",
    description:
      "Soma do tempo de atenção das zonas selecionadas no período filtrado.",
    prefix: "",
    suffix: "",
    formatter: "duration",
  },
  {
    key: "peak_people",
    title: "Pico de pessoas",
    description:
      "Número máximo (pico) simultâneo de pessoas, considerando todas as zonas da loja. Essa métrica não é afetada pelos filtros de zonas e categoria, apenas pelos filtros de período e data.",
    prefix: "",
    suffix: "",
  },
  {
    key: "occupancy_rate",
    title: "Porcentagem de ocupação",
    description:
      "Porcentagem do tempo total em que a loja esteve ocupada. É calculada pela contagem dos minutos com pelo menos uma pessoa dividida pelo total de minutos em que a loja esteve aberta, com base no horário de funcionamento. Essa métrica não é afetada pelos filtros de zonas e categoria, apenas pelos filtros de período e data.",
    prefix: "",
    suffix: "%",
  },
];

export const FLOW_DETECTIONS_TABLE_COLUMNS = [
  {
    title: "Descrição",
    dataIndex: "description",
    key: "description",
    width: "auto",
    ellipsis: true,
  },
  {
    title: "Atenção",
    dataIndex: "permanence",
    key: "permanencia",
    width: "auto",
    ellipsis: false,
    defaultSortOrder: "descend",
    sorter: (a, b) =>
      parseNumberValue(a.permanence) - parseNumberValue(b.permanence),
    render: (value) => formatarDuracao(parseNumberValue(value)),
  },
  {
    title: (
      <Tooltip title="Porcentagem do tempo em que a zona esteve com pelo menos uma pessoa presente, dividida pelo total de minutos em que a loja esteve aberta no período.">
        Ocupação
      </Tooltip>
    ),
    dataIndex: "ocupation",
    key: "ocupacao",
    width: "auto",
    ellipsis: true,
    render: (value) => `${formatOneDecimalPtBr(parsePercentValue(value))} %`,
  },
  {
    title: "Pico",
    dataIndex: "peak",
    key: "pico",
    width: "auto",
    ellipsis: true,
    render: (value) => formatIntegerPtBr(parseNumberValue(value)),
  },
];

export const QUEUE_ZONES_TABLE_COLUMNS = [
  {
    title: "Zona",
    dataIndex: "zone_name",
    key: "zone_name",
    width: "auto",
    ellipsis: true,
  },
  {
    title: "Tempo médio de espera",
    dataIndex: "espera_media_min",
    key: "espera_media_min",
    width: "auto",
    render: (value) => (value != null ? formatarDuracao(value) : "—"),
  },
  {
    title: "Pessoas",
    dataIndex: "pessoas",
    key: "pessoas",
    width: "auto",
    render: (value) => formatIntegerPtBr(value),
  },
];

export const PEAK_HOURS_TABLE_COLUMNS = [
  {
    title: "Horário",
    dataIndex: "hour",
    key: "hour",
    width: "auto",
    ellipsis: true,
  },
  {
    title: "Atenção",
    dataIndex: "permanence",
    key: "permanence",
    width: "auto",
    ellipsis: true,
    render: (value) => formatarDuracao(parseNumberValue(value)),
  },
  {
    title: "Ocupação",
    dataIndex: "occupation",
    key: "occupation",
    width: "auto",
    ellipsis: false,
    render: (value) => `${formatOneDecimalPtBr(parsePercentValue(value))} %`,
  },
  {
    title: "Pico",
    dataIndex: "peak",
    key: "peak",
    width: "auto",
    ellipsis: true,
    render: (value) => formatIntegerPtBr(parseNumberValue(value)),
  },
];

export const REVENUE_KPIS_METRICS_DESCRIPTION = {
  faturamento_total:
    "Faturamento total da loja selecionada durante o período de análise. São consideradas apenas as vendas presenciais.",
  ticket_medio: "Valor médio faturado por venda.",
  faturamento_item: "Valor da média de cada item vendido na loja.",
  receita_tempo_permanencia:
    "Receita calculada pelo tempo de atenção nas zonas filtradas.",
  conversao:
    "Métrica que relaciona a quantidade de vendas efetuadas pela quantidade de pessoas que entram na loja.",
  quantidade_vendas:
    "Quantidade total de vendas presenciais da loja selecionada no intervalo de datas filtrado.",
};

export const MENU_ITEMS_DESCRIPTION = {
  ai_reports: "Rotinas, prompts e relatórios semanais gerados por IA",
  ai_agent: "Configuração, uso e conversas do agente de IA do chat",
  contatos:
    "Contatos dos clientes: filiais, relatórios e conversa com o agente",
  flow_detections: "Métricas do fluxo geral de pessoas na loja",
  collections_change:
    "Métricas de aumento/diminuição do fluxo de pessoas após eventos",
  promocoes_por_sku:
    "Desempenho de cada SKU em promoção: faturamento, unidades e exposição",
  peak_hours: "Métricas sobre horários de maior movimento",
  events_manager: "Criação/edição de categorias e eventos",
  zones_manager: "Criação/edição de zonas e câmeras",
  revenue: "Métricas sobre o faturamento da loja",
  opening_hours:
    "Gerencie metas de faturamento, horários de funcionamento e dados da loja",
  control_panel: "Gerencie o quadro de lojas e permissões de usuários",
  lost_sales:
    "Rodízio de atendimento ao vivo e análise dos motivos de não venda",
};

export const HOME_DASHBOARD_ZONES_CARDS_KPIS = [
  {
    title: "Faturamento",
    key: "first_zones",
    group: "revenue",
  },
  {
    title: "Faturamento",
    key: "last_zones",
    group: "revenue",
  },
  {
    title: "Atenção",
    key: "first_zones",
    group: "occupation",
  },
  {
    title: "Atenção",
    key: "last_zones",
    group: "occupation",
  },
  {
    title: "Faturamento por Atenção",
    key: "first_zones",
    group: "revenue_per_minute",
  },
  {
    title: "Faturamento por Atenção",
    key: "last_zones",
    group: "revenue_per_minute",
  },
];

export const HOME_DASHBOARD_COLLECTIONS_CARDS_KPIS = [
  {
    title: "Faturamento",
    key: "first_collections",
    group: "revenue",
  },
  {
    title: "Faturamento",
    key: "last_collections",
    group: "revenue",
  },
  {
    title: "Exposição",
    key: "first_collections",
    group: "occupation",
  },
  {
    title: "Exposição",
    key: "last_collections",
    group: "occupation",
  },
  {
    title: "Faturamento por Exposição",
    key: "first_collections",
    group: "revenue_per_minute",
  },
  {
    title: "Faturamento por Exposição",
    key: "last_collections",
    group: "revenue_per_minute",
  },
];

export const HOME_DASHBOARD_CARDS = [
  {
    title: "Total Faturado",
    description: HOME_DASHBOARD_METRICS_DESCRIPTION["faturamento_total"],
    key: "faturamento_total",
    tagValue: "variacao_faturamento_total",
    formatter: "currency",
  },
  {
    title: "Faturamento por Atenção",
    description:
      "Faturamento presencial dos dias com câmera dividido pelo tempo de atenção (pessoas × minutos) nas zonas da loja.",
    key: "faturamento_por_tempo_permanencia",
    tagValue: "variacao_faturamento_por_tempo_permanencia",
    suffix: "R$/min",
    formatter: "number_br",
  },
  {
    title: "Oportunidades",
    description: HOME_DASHBOARD_METRICS_DESCRIPTION["oportunidades"],
    tagValue: "variacao_oportunidades",
    key: "oportunidades",
  },
  {
    title: "Conversão",
    description: HOME_DASHBOARD_METRICS_DESCRIPTION["conversao_oportunidades"],
    key: "conversao",
    tagValue: "variacao_conversao",
    tagSuffix: "p.p.",
    suffix: "%",
  },
];

export const PROMO_TOOLTIPS = {
  escopo:
    "Zona: o evento é analisado pelo faturamento rateado e pela atenção da zona. SKUs: pela venda direta dos produtos listados, sem rateio. Categoria: pela venda direta dos SKUs que estão na categoria a cada dia da vigência (o número é o de hoje); no modo Ouran a categoria vem do catálogo por código de barras, no modo ERP é a coleção. Zonas + Categoria ou SKUs: faturamento dos produtos e atenção das zonas.",
  faturamento_pct:
    "Variação da média diária de faturamento depois do início contra a mesma quantidade de dias abertos antes. No evento por zona o faturamento é rateado entre as zonas da categoria; no evento por produto é a venda direta dos SKUs.",
  atencao_pct:
    "Variação da média diária de tempo de atenção nas zonas do evento. Evento só por produto não tem atenção: permanência e conversão são medidas por zona.",
  delta_total:
    "Diferença em reais entre o faturamento depois e antes do início, na mesma quantidade de dias abertos. Com período comparativo maior ou menor que o evento, é a diferença de média diária multiplicada pelos dias do evento.",
  execucao_pct:
    "Parte do faturamento dos SKUs da campanha que saiu com o desconto da promoção reconhecido.",
  cobertura_pct:
    "Parte dos SKUs da campanha que teve ao menos uma venda com o desconto da promoção.",
  status:
    "Na ordem de prioridade. Sem alteração de preço: a promoção dá o mesmo desconto que o produto já tinha no cadastro. Ruptura: estoque zerado no último retrato do ERP. Sem venda no período: nenhuma venda na vigência. Vendeu com desconto: o caso normal.",
  sku_vendas:
    "Unidades vendidas no período da campanha e a variação sobre o período anterior.",
  sku_faturamento:
    "Faturamento no período da campanha e a variação sobre o período anterior.",
  sku_margem:
    "Faturamento menos custo no período da campanha e a variação sobre o período anterior. A lista vem ordenada pela variação da margem em reais, para os SKUs de ticket alto não subirem só pelo tamanho.",
  sku_atencao:
    "Minutos de exposição, sem vendedores, nas zonas em que o SKU estava exposto no período da campanha e a variação sobre o período anterior. Sem vínculo do SKU com zona, mostra a exposição da categoria, sem variação.",
  desconto_implicito_pct:
    "Desconto calculado pelo preço da promoção contra o preço de tabela, quando o ERP não informa o percentual.",
  desconto_permanente_pct:
    "Desconto que o produto já tem no cadastro do ERP, fora da promoção. Não é efeito da campanha.",
  estoque: "Estoque do último retrato do ERP.",
  exposicao_minutos:
    "Minutos de atenção, sem vendedores, nas zonas em que o SKU estava exposto durante a vigência: planograma do Jasper pelo EAN ou vínculo manual do SKU a uma categoria. Cada zona conta inteira para cada SKU, então não some entre SKUs.",
  exposicao_r_por_min:
    "Faturamento do SKU na vigência dividido pelos minutos de exposição.",
  lift_unidades_pct:
    "Variação da média diária de unidades vendidas na vigência contra a mesma quantidade de dias abertos antes dela.",
  comparativo:
    "Período anterior e período da campanha em dias abertos com venda na loja, os mesmos do lift. O período comparativo do cabeçalho muda o tamanho do período anterior.",
  comparativo_preco_medio:
    "Faturamento dividido pelas unidades vendidas no período: o preço médio praticado no cupom, não o preço de tabela.",
  comparativo_custo_unitario:
    "Custo informado pelo ERP nas vendas do período, dividido pelas unidades. Fica indisponível quando alguma venda veio sem custo.",
  comparativo_margem_unitaria: "Preço médio menos o custo unitário do período.",
  comparativo_exposicao_min:
    "Minutos de atenção, sem vendedores, nas zonas em que o SKU estava exposto nos dias do período. Indisponível quando o SKU não tem vínculo com zona.",
  comparativo_margem:
    "Faturamento menos o custo das vendas do período. Fica indisponível quando alguma venda veio sem custo.",
  comparativo_variacao:
    "Variação do período da campanha sobre o período anterior. Em totais (unidades, faturamento, margem e atenção) compara a média diária quando os períodos têm quantidades diferentes de dias.",
};

export const PROMO_KPIS_METRICS = [
  {
    key: "faturamento_vendas",
    title: "Faturamento da campanha",
    description:
      "Soma das vendas dos SKUs da campanha na vigência, até ontem. Devoluções ficam de fora.",
    suffix: "",
    formatter: "currency",
    variationKey: "variacao_faturamento_vendas",
    variationUnit: "pct",
  },
  {
    key: "margem",
    title: "Margem",
    description:
      "Faturamento menos o custo das vendas. Fica indisponível quando alguma venda veio sem custo.",
    suffix: "",
    formatter: "currency",
    variationKey: "variacao_margem",
    variationUnit: "pct",
  },
  {
    key: "margem_pct",
    title: "Margem %",
    description: "Margem sobre o faturamento da campanha.",
    suffix: "%",
    variationKey: "variacao_margem_pct",
    variationUnit: "pp",
  },
  {
    key: "desconto_concedido",
    title: "Desconto concedido",
    description:
      "Queda do preço médio entre a Base e a Vigência, aplicada às unidades vendidas na campanha.",
    suffix: "",
    formatter: "currency",
  },
  {
    key: "unidades",
    title: "Unidades vendidas",
    description: "Unidades vendidas dos SKUs da campanha na vigência.",
    suffix: "",
    variationKey: "variacao_unidades",
    variationUnit: "pct",
  },
  {
    key: "entradas",
    title: "Entradas",
    description:
      "Entradas de clientes na filial nos dias da campanha, excluindo leituras identificadas como equipe.",
    suffix: "",
    variationKey: "variacao_entradas",
    variationUnit: "pct",
  },
  {
    key: "execucao-cobertura",
    metrics: [
      {
        key: "execucao_pct",
        title: "Execução",
        description: PROMO_TOOLTIPS.execucao_pct,
        suffix: "%",
      },
      {
        key: "cobertura_pct",
        title: "Cobertura",
        description: PROMO_TOOLTIPS.cobertura_pct,
        suffix: "%",
      },
    ],
  },
  {
    key: "esgotados",
    title: "SKUs esgotados",
    description:
      "SKUs com estoque zerado ou negativo no último retrato do ERP, tenham vendido ou não.",
    suffix: "",
  },
];
